/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { Box, Flex, Radio, RadioGroup, Stack, IconButton, Text, Tooltip, useToast, Center } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { gql, useLazyQuery } from '@apollo/client';
import { DownloadIcon } from '@chakra-ui/icons';
import { chartBackgroundColor } from '../../Miscellaneous/plugins-chart';
import { formatNumber } from '../../../../utils/format';
import Chart from 'react-chartjs-2';
import DataLoading from '../../../DataLoading';

const Q_DATASET = gql`
	query get($companyType: InsuranceInstitutionsType, $companyId: String, $range: MDDataDateRangeInput, $type: MDDataQuarterProductEnumType!) {
		getQuarterlyClaimPaidVsClaimIncurredDashboardDataset(companyType: $companyType, companyId: $companyId, range: $range, type: $type)
	}
`;

function Graph({ companyType = null, companyId = null, range = null }) {
	const chartRef = useRef(null);
	const toast = useToast();
	const [type, setType] = useState('Quarter');
	const { t } = useTranslation();
	const [getDataset, { data, error, loading }] = useLazyQuery(Q_DATASET, { fetchPolicy: 'no-cache' });

	useEffect(load, [companyType, companyId, range, type]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	function load() {
		const variables = { companyType, companyId, range: range?.from && range?.to ? range : null, type };
		getDataset({ variables });
	}

	return (
		<Box className={loading ? 'disableFormClear' : ''}>
			<Box border="1px dotted #bdc3c7">
				<Flex justifyContent="center" alignItems="center" flexDirection="column" p={4}>
					<Text fontSize="lg" fontWeight="bold" color="gray.600" textAlign="center" pl={4} pr={4}>
						{t('Claim Paid')}
					</Text>
					<Text fontSize="lg" fontWeight="bold" color="gray.600" textAlign="center" pl={4} pr={4}>
						{t('Versus')}
					</Text>
					<Text fontSize="lg" fontWeight="bold" color="gray.600" textAlign="center" pl={4} pr={4}>
						{t('Claim Incurred')}
					</Text>
				</Flex>
				<DataLoading loading={loading} loadedData={data?.getQuarterlyClaimPaidVsClaimIncurredDashboardDataset}>
					{({ loadedData }) => (
						<>
							<Flex justifyContent="center" alignItems="center" flexDirection="column">
								<RadioGroup onChange={setType} value={type}>
									<Stack direction="row">
										<Text fontSize="sm">{t('Show By')}</Text>
										<Radio size="sm" value="Quarter">
											{t('Quarter')}
										</Radio>
										<Radio size="sm" value="Product">
											{t('Product')}
										</Radio>
									</Stack>
								</RadioGroup>
							</Flex>
							<Box w="100%" p={4} pt={0}>
								<Center>
									<Chart
										ref={chartRef}
										type="bar"
										height="300px"
										plugins={[chartBackgroundColor]}
										options={{
											barPercentage: 1,
											maintainAspectRatio: false,
											canvasBackgroundColor: {
												color: '#FFFFFF',
											},
											layout: {
												padding: {
													top: 32,
													right: 16,
													bottom: 16,
													left: 8,
												},
											},
											plugins: {
												title: { display: false },
												legend: {
													display: true,
													labels: {
														font: {
															family: 'Rajdhani, Hanuman, sans-serif, Moul',
															weight: 'normal',
															size: 12,
														},
														generateLabels(chart) {
															return chart.data.datasets.map((el, i) => {
																const meta = chart.getDatasetMeta(i);
																const style = meta.controller.getStyle(i);
																return {
																	text: t(el?.label),
																	fillStyle: style.backgroundColor,
																	strokeStyle: style.borderColor,
																	lineWidth: style.borderWidth / 4,
																	pointStyle: chart.legend.options.labels.pointStyle,
																	hidden: !chart.getDataVisibility(i),
																	index: i,
																};
															});
														},
														usePointStyle: true,
														pointStyle: 'rect',
													},
													position: 'bottom',
												},
												tooltip: {
													callbacks: {
														label: (toolTipItem) => {
															return t(toolTipItem.dataset.label) + ': $' + toolTipItem.formattedValue;
														},
													},
												},
											},
											scales: {
												y: {
													ticks: {
														callback: function (value, index, ticks) {
															return '$' + formatNumber(value);
														},
													},
												},
											},
										}}
										data={loadedData}
									/>
								</Center>
							</Box>
							<Flex className="hidden-print" justifyContent="flex-end" alignItems="center" p={4}>
								<Tooltip label={t('Download Image')}>
									<IconButton
										size="sm"
										icon={<DownloadIcon w={3} h={3} />}
										onMouseDown={() => {
											const link = document.createElement('a');
											link.download = `graph-claim-paid-claim-incurred.jpg`;
											link.href = chartRef?.current?.toBase64Image('image/jpeg', 1);
											link.click();
										}}
									/>
								</Tooltip>
							</Flex>
						</>
					)}
				</DataLoading>
			</Box>
		</Box>
	);
}

export default Graph;
